<template>
  <div class="fees" :class="{ 'fees--selected': selected }" @click="handleClick">
    <!-- 最划算标签 -->
    <div class="fees__tag" v-if="showBestTag">
      <IconGood class="fees__tag-icon" />
      <span>最划算</span>
    </div>

    <div class="fees__content">
      <div class="fees__name">{{ packageNameText }}</div>

      <!-- 价格部分 -->
      <div class="fees__cost">
        <span class="fees__price"
          >¥<RollingNumber
            :precision="2"
            :value="
              showRightPrice
                ? displayPrice.priceDividedByMonths.value
                : displayPrice.originalPriceDividedByMonths.value
            " /></span
        ><span class="fees__price-cycle" v-if="displayPrice.cycle">/月</span>
        <div class="fees__original-price" v-if="displayPrice.originalPrice.value > 0">
          ¥{{ displayPrice.originalPriceDividedByMonths.value }}/月
        </div>
      </div>

      <!-- 总价和立省信息 -->
      <div class="fees__savings" v-if="displayPrice.originalPrice.value === 0">
        <span class="fees__savings-total">原价购买</span>
      </div>
      <div class="fees__savings" v-else-if="isPayment">
        <span class="fees__savings-total">总价{{ displayPrice.price.string }}，立省</span>
        <span class="fees__savings-highlight">{{ displayPrice.priceSaved.string }}</span>
      </div>
      <div class="fees__savings" v-else-if="isSubscribe">
        <span class="fees__savings-total">限时特惠，立省</span>
        <span class="fees__savings-highlight"
          >{{ displayPrice.priceSaved.string }}/{{ displayPrice.cycle }}</span
        >
      </div>

      <!-- 功能列表 -->
      <div class="fees__features">
        <div class="fees__feature-item">
          <IconCheck class="fees__feature-icon" />
          <span class="fees__feature-text" v-if="isPayment && isAnnualPlan">
            一次性发放{{ props.fees.credit_coin }}言币
          </span>
          <span class="fees__feature-text" v-else-if="isMonthlyPlan">
            每{{ displayPrice.cycle }}{{ props.fees.credit_coin }}言币
          </span>
        </div>
        <div class="fees__feature-item">
          <IconCheck class="fees__feature-icon" />
          <span class="fees__feature-text"> {{ exportTimeLimit }} </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getI18nService } from '@rendora/common/services/i18n';
import { type IFees } from '@rendora/common/store/rights';
import { BUY_TYPE, IRightsType } from '@rendora/common/types/enums';
import { formatPrice } from '@rendora/common/utils/transform';
import IconCheck from '@rendora/design/assets/check.svg?component';
import IconGood from '@rendora/design/assets/good.svg?component';
import RollingNumber from '@rendora/design/components/rolling-number/index.vue';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { usePayment } from '../../../store';

const paymentStore = usePayment();

const { isAcceptedDiscount, isDiscountAvailable } = storeToRefs(paymentStore);

const showRightPrice = computed(() => {
  return !isDiscountAvailable.value || isAcceptedDiscount.value;
});

const i18nService = getI18nService();
const $t = i18nService.t;

const exportTimeLimit = computed(() => {
  const time = IRightsType.PERSONAL === props.rightType ? 10 : 5;
  return `单条视频 ≤ ${time}分钟`;
});

// 入参定义
const props = withDefaults(
  defineProps<{
    fees: IFees;
    selected: boolean;
    discount?: string;
    rightType: IRightsType;
  }>(),
  {},
);

// 事件定义
const emits = defineEmits<{
  'on-select': [IFees];
}>();

// 计划类型辅助标识
const isSubscribe = computed(() => props.fees.buy_type === BUY_TYPE.SUBSCRIBE);
const isPayment = computed(() => props.fees.buy_type === BUY_TYPE.PAYMENT);
const isMonthlyPlan = computed(() => props.fees.days === 30);
const isAnnualPlan = computed(() => props.fees.days === 365);

const packageNameText = computed(() => {
  if (isPayment.value) {
    if (isMonthlyPlan.value) return '月度会员';
    if (isAnnualPlan.value) return '年度会员';
  }
  if (isSubscribe.value) {
    if (isMonthlyPlan.value) return '连续包月';
    if (isAnnualPlan.value) return '连续包年';
  }
  return '';
});

const displayPrice = computed(() => {
  let priceDividedByMonths = props.fees.price / (props.fees.months || 1);
  if (isAnnualPlan.value) {
    priceDividedByMonths += 0.01;
  }
  return {
    cycle: isMonthlyPlan.value ? $t('yue') : $t('nian'),
    price: formatPrice(props.fees.price),
    priceDividedByMonths: formatPrice(priceDividedByMonths),
    originalPrice: formatPrice(props.fees.show_price),
    originalPriceDividedByMonths: formatPrice(props.fees.show_price / (props.fees.months || 1)),
    priceSaved: formatPrice(props.fees.show_price - props.fees.price),
  };
});

const showBestTag = computed(() => {
  return props.fees.days === 365 && props.fees.buy_type === BUY_TYPE.PAYMENT;
});

// 点击选择当前费用卡片
function handleClick() {
  emits('on-select', props.fees);
}
</script>

<style lang="scss" scoped>
.fees {
  cursor: pointer;
  width: 208px;
  position: relative;
  height: 190px;
  border-radius: var(--x-radius-medium);
  background-color: var(--x-back-0);
  border: 1px solid var(--x-back-4);
  transition: border-color 0.3s ease;
  &:not(.fees--selected):hover {
    border-color: var(--x-purple-6);
  }
}

.fees--selected {
  border: 1px solid var(--x-purple-6);
  background-color: var(--x-purple-1);
}

.fees__content {
  padding: 16px;
}

.fees__name {
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  font-weight: var(--x-weight-normal);
  color: var(--x-text-3);
  margin-bottom: 16px;
}

.fees__tag {
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  width: 67px;
  height: 22px;
  position: absolute;
  color: var(--x-white-1);
  right: 8px;
  top: 8px;
  background-color: var(--x-error-6);
  border-radius: var(--x-radius-small);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
}

.fees__tag-icon {
  font-size: 16px;
  color: var(--x-white-1);
}

.fees__cost {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
}

.fees__price {
  font-size: var(--x-text-24-size);
  line-height: var(--x-text-24-line-height);
  color: var(--x-text-1);
  font-weight: var(--x-weight-medium);
}

.fees__price-cycle {
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
  color: var(--x-text-1);
  font-weight: var(--x-weight-normal);
}

.fees__original-price {
  font-size: 12px;
  line-height: 18px;
  text-decoration: line-through;
  color: var(--x-text-3);
  opacity: 0.4;
  margin-bottom: 8px;
}

.fees__savings {
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 16px;
}

.fees__savings-total {
  color: var(--x-text-3);
}

.fees__savings-highlight {
  color: var(--x-purple-6);
}

.fees__features {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fees__feature-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.fees__feature-icon {
  width: 16px;
  height: 16px;
  color: #52c41a;
  flex-shrink: 0;
}

.fees__feature-text {
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  color: var(--x-text-5);
}
</style>
